/**
 * Tournament progress indicator component.
 */

import { useTournament } from './TournamentProvider';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Filter, Users, Swords, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';

export function TournamentProgress() {
  const { state, progress } = useTournament();

  if (!state) return null;

  // Calculate overall progress percentage
  let percentage = 0;
  if (state.status === 'group_stage') {
    // Group stage: count completed groups across all rounds
    const completedGroups = state.currentGroupIndex;
    const totalGroups = state.groups.length;
    // Rough estimate: assume 50% of tournament is group stage
    percentage = Math.round((completedGroups / totalGroups) * 50);
  } else if (state.status === 'bracket_stage' && state.bracket) {
    // Bracket stage: count decided matches
    const decidedMatches = state.bracket.filter(m => m.winner !== null).length;
    const totalMatches = state.bracket.length;
    // Bracket is the other 50%
    percentage = 50 + Math.round((decidedMatches / totalMatches) * 50);
  } else if (state.status === 'completed') {
    percentage = 100;
  }

  const statusLabel =
    state.status === 'filter_stage'
      ? 'Select Countries'
      : state.status === 'group_stage'
        ? 'Group Stage'
        : state.status === 'bracket_stage'
          ? 'Knockout Stage'
          : state.status === 'completed'
            ? 'Completed'
            : 'Not Started';

  const StatusIcon =
    state.status === 'filter_stage'
      ? Filter
      : state.status === 'group_stage'
        ? Users
        : state.status === 'bracket_stage'
          ? Swords
          : Trophy;

  return (
    <div className="hidden sm:flex items-center gap-3 px-4 py-3 bg-muted/50 rounded-lg border">
      {/* Status badge */}
      <Badge
        variant={state.status === 'completed' ? 'default' : 'secondary'}
        className={cn(
          'flex items-center gap-1.5',
          state.status === 'completed' && 'bg-green-500'
        )}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        {statusLabel}
      </Badge>

      {/* Progress bar */}
      <div className="flex-1">
        <Progress value={percentage} className="h-2" />
      </div>

      {/* Progress text */}
      <div className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
        {state.status === 'filter_stage' && (
          <span>
            {state.filterSelection?.length ?? 0} of {state.totalCountries} selected
          </span>
        )}
        {state.status === 'group_stage' && (
          <span>
            Round {progress.groupRound} &bull; Group {progress.currentGroup}/{progress.totalGroups}
          </span>
        )}
        {state.status === 'bracket_stage' && (
          <span>
            {progress.countriesRemaining} remaining
          </span>
        )}
        {state.status === 'completed' && (
          <span className="text-green-600 font-medium">
            Tournament Complete!
          </span>
        )}
      </div>
    </div>
  );
}
